import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { getStudentDetail } from './api/studentsApi'
import { getReportCards, getReportTerms } from './api/studentScreensApi'

export default function StudentReportCardScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [reportCards, setReportCards] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTerm, setSelectedTerm] = useState('')

  useEffect(() => {
    loadStudent()
    loadReportCards()
  }, [studentId])

  const loadStudent = async () => {
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch (err) {
      appToast.error('Failed to load student')
    }
  }

  const loadReportCards = async () => {
    try {
      setIsLoading(true)
      const terms = await getReportTerms(studentId)
      const cards = await getReportCards(studentId)
      setReportCards(cards || [])
      if (terms && terms.length > 0) {
        setSelectedTerm(terms[0].term)
      } else if (cards && cards.length > 0) {
        setSelectedTerm(cards[0].term)
      }
    } catch (err) {
      appToast.error('Failed to load report cards')
    } finally {
      setIsLoading(false)
    }
  }

  const goBack = () => navigate('/admission/students/list')

  const handlePrint = () => {
    window.print()
  }

  const handleDownload = () => {
    window.print()
  }

  const selectedCard = reportCards.find((rc) => rc.term === selectedTerm)

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'bg-emerald-100 text-emerald-700',
      'A': 'bg-emerald-100 text-emerald-700',
      'B+': 'bg-blue-100 text-blue-700',
      'B': 'bg-blue-100 text-blue-700',
      'C': 'bg-amber-100 text-amber-700',
      'D': 'bg-orange-100 text-orange-700',
      'F': 'bg-rose-100 text-rose-700',
    }
    return colors[grade] || 'bg-slate-100 text-slate-700'
  }

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      {/* Header */}
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Report Card</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            {student ? `${student.firstName} ${student.lastName}` : 'Student'} - Report Card
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      {/* Term Selector */}
      {isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low px-6 py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
          <p className="mt-3 text-sm text-on-surface-variant">Loading report cards...</p>
        </div>
      ) : reportCards.length === 0 ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low px-6 py-12 text-center">
          <span className="material-symbols-outlined text-4xl text-outline">school</span>
          <p className="mt-3 text-sm font-medium text-on-surface-variant">No report cards available.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
            <h3 className="mb-3 text-sm font-bold text-on-surface">Select Term</h3>
            <div className="flex flex-wrap gap-3">
              {reportCards.map((card) => (
                <button
                  key={card.term}
                  onClick={() => setSelectedTerm(card.term)}
                  className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors ${
                    selectedTerm === card.term
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-variant text-on-surface-variant hover:bg-outline-variant/50'
                  }`}
                >
                  {card.term}
                </button>
              ))}
            </div>
          </div>

          {/* Report Card Details */}
          {selectedCard && (
            <div className="space-y-6">
              {/* GPA Summary */}
              <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase text-on-surface-variant">Cumulative GPA</p>
                    <p className="mt-2 text-3xl font-bold text-on-surface">{selectedCard.gpa}</p>
                    <p className="mt-2 text-xs text-on-surface-variant">
                      Published on {new Date(selectedCard.date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handlePrint(selectedCard.term)}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-on-primary hover:bg-primary/90"
                    >
                      <span className="material-symbols-outlined text-base">print</span>
                      Print
                    </button>
                    <button
                      onClick={() => handleDownload(selectedCard.term)}
                      className="inline-flex items-center gap-2 rounded-lg bg-secondary-container px-4 py-2 text-sm font-bold text-secondary hover:bg-secondary-container/80"
                    >
                      <span className="material-symbols-outlined text-base">download</span>
                      Download
                    </button>
                  </div>
                </div>
              </div>

              {/* Marks Table */}
              <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-low shadow-xs">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-surface-container-lowest">
                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-on-surface-variant">Subject</th>
                        <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-on-surface-variant">Score</th>
                        <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-on-surface-variant">Grade</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/20">
                      {selectedCard.marks.map((mark, idx) => (
                        <tr key={idx} className="hover:bg-surface-container-lowest">
                          <td className="px-6 py-4 text-sm text-on-surface">{mark.subject}</td>
                          <td className="px-6 py-4 text-center text-sm font-semibold text-on-surface">{mark.score}</td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-bold ${getGradeColor(mark.grade)}`}>
                                {mark.grade}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
